import {
  RefObject,
  ChangeEvent,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { reduce, filter, isArray, includes, isEqual, has } from 'lodash';
import moment from 'moment';

import { Nullable } from '~globals/types';
import { PlanFeaturesTypes } from '~globals/types/enums';
import {
  BillingItemCompanyBase,
  SettlementBillingItemsFilter,
} from '~services/billingItem/types';
import { CustomAutocompleteRef } from '~components/CustomAutocomplete/types';
import {
  useSubscriptionFeatures,
  useParseLocationQuery,
  useFiltersValues,
} from '~hooks/index';
import { FiltersValuesReturn } from '~hooks/useFiltersValues';
import { getFeatureAvailableToDate } from '~utils/feature';
import { getItemValue } from '~utils/helpers';
import { formatterDate } from '~utils/formatter';
import { getCompanyToBillingItemId } from '~utils/billingItem';

export interface OrderBillingHistoricalListQueryParamsFilters {
  batchNumber?: string;
  company?: string;
  integrationStores?: string | string[];
  giverCompanies?: string | string[];
  dateFrom?: Date;
  dateTo?: Date;
}

export interface OrderBillingHistoricalListFilters {
  batchNumber: Nullable<string>;
  company: Nullable<BillingItemCompanyBase>;
  integrationStores: Nullable<BillingItemCompanyBase[]>;
  giverCompanies: Nullable<BillingItemCompanyBase[]>;
  dateFrom: Nullable<Date>;
  dateTo: Nullable<Date>;
}

type OrderBillingHistoricalListFiltersAppliedType = 'queryParams' | 'filters';

type OrderBillingHistoricalFiltersApplied = Record<
  keyof OrderBillingHistoricalListFilters,
  OrderBillingHistoricalListFiltersAppliedType
>;

export type OrderBillingHistoricalListAutocompleteMultipleRef<T> =
  CustomAutocompleteRef<T, true>;

export interface OrderBillingHistoricalListParamsSelectData<T> {
  autocompleteRef: RefObject<
    OrderBillingHistoricalListAutocompleteMultipleRef<T>
  >;
  loading: boolean;
  list: T[];
}

export interface OrderBillingHistoricalListParamsSelects {
  companies: OrderBillingHistoricalListParamsSelectData<BillingItemCompanyBase>;
}

interface OrderBillingHistoricalListParams {
  initialFilters: OrderBillingHistoricalListFilters;
  selectsData: OrderBillingHistoricalListParamsSelects;
}

export interface OrderBillingHistoricalListReturn
  extends FiltersValuesReturn<OrderBillingHistoricalListFilters> {
  parseFiltersRequest: SettlementBillingItemsFilter;
}

export const useOrderBillingHistoricalListFilters = ({
  initialFilters,
  selectsData,
}: OrderBillingHistoricalListParams): OrderBillingHistoricalListReturn => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const hasAccesMgmBillingGiver = useMemo(
    () =>
      hasAccessToFeature(
        PlanFeaturesTypes.MgmBillingGiver,
        getFeatureAvailableToDate(PlanFeaturesTypes.MgmBillingGiver),
      ),
    [hasAccessToFeature],
  );

  const queryFilters =
    useParseLocationQuery<OrderBillingHistoricalListQueryParamsFilters>({
      parseNumbers: false,
    });

  const [filtersApplied, setFilterApplied] =
    useState<OrderBillingHistoricalFiltersApplied>(() => {
      const builder = reduce(
        initialFilters,
        (acc, _, key) => {
          const keyTyped = key as keyof OrderBillingHistoricalListFilters;

          acc[keyTyped] = has(queryFilters, keyTyped)
            ? 'queryParams'
            : 'filters';

          return acc;
        },
        {} as OrderBillingHistoricalFiltersApplied,
      );

      return builder;
    });

  const { filters, setFilterByKey, clearFilters } =
    useFiltersValues(initialFilters);

  const getFilterAppliedByKey = useCallback(
    <Key extends keyof OrderBillingHistoricalFiltersApplied>(
      key: Key,
    ): OrderBillingHistoricalFiltersApplied[Key] =>
      getItemValue(filtersApplied, key),
    [filtersApplied],
  );

  const setFilterAppliedByKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, value: any) => {
      setFilterByKey(key, value);

      const keyTyped = key as keyof OrderBillingHistoricalListFilters;

      setFilterApplied((prevFiltersApplied) => ({
        ...prevFiltersApplied,
        [keyTyped]: 'filters',
      }));
    },
    [setFilterByKey],
  );

  const handleChangeInputFilterApplied = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target ?? event.currentTarget;

      setFilterAppliedByKey(name, value);
    },
    [setFilterAppliedByKey],
  );

  const clearFiltersApplied = useCallback(() => {
    clearFilters();

    setFilterApplied({
      batchNumber: 'filters',
      company: 'filters',
      integrationStores: 'filters',
      giverCompanies: 'filters',
      dateFrom: 'filters',
      dateTo: 'filters',
    });
  }, [clearFilters, setFilterApplied]);

  const getValueQueryParams = useCallback(
    <Key extends keyof OrderBillingHistoricalListQueryParamsFilters>(
      key: Key,
    ): OrderBillingHistoricalListQueryParamsFilters[Key] =>
      getItemValue(queryFilters, key),
    [queryFilters],
  );

  const getValueInitialFilters = useCallback(
    <Key extends keyof OrderBillingHistoricalListFilters>(
      key: Key,
    ): OrderBillingHistoricalListFilters[Key] =>
      getItemValue(initialFilters, key),
    [initialFilters],
  );

  const getValueCurrentFilters = useCallback(
    <Key extends keyof OrderBillingHistoricalListFilters>(
      key: Key,
    ): OrderBillingHistoricalListFilters[Key] => getItemValue(filters, key),
    [filters],
  );

  const setBatchNumberByQueryParam = useCallback(() => {
    const currentBatchNumberQueryParam = getValueQueryParams('batchNumber');
    const currentBatchNumberDefault = getValueInitialFilters('batchNumber');

    const parseBatchNumber =
      currentBatchNumberQueryParam || currentBatchNumberDefault;

    setFilterByKey(
      'batchNumber',
      parseBatchNumber ? String(parseBatchNumber) : null,
    );
  }, [getValueQueryParams, getValueInitialFilters, setFilterByKey]);

  useEffect(() => {
    if (hasAccesMgmBillingGiver) {
      setBatchNumberByQueryParam();
    }
  }, [hasAccesMgmBillingGiver, setBatchNumberByQueryParam]);

  const getFilteredArrayValueByQueryParam = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>>({
      key,
      currentSelectData,
      getExtractorKey,
    }: {
      key: 'company' | 'integrationStores' | 'giverCompanies';
      currentSelectData: OrderBillingHistoricalListParamsSelectData<T>;
      getExtractorKey: (data: T) => string;
    }) => {
      const currentArrayQueryParam = getValueQueryParams(key);

      if (
        currentArrayQueryParam !== undefined &&
        currentArrayQueryParam !== null &&
        !currentSelectData.loading
      ) {
        const filteredValues = filter(
          currentSelectData.list,
          (currentOption) => {
            const currentOptionId = getExtractorKey(currentOption);

            if (isArray(currentArrayQueryParam)) {
              return includes(currentArrayQueryParam, currentOptionId);
            }

            return isEqual(currentArrayQueryParam, currentOptionId);
          },
        );

        return filteredValues;
      }

      return null;
    },
    [getValueQueryParams],
  );

  const setCompaniesByQueryParam = useCallback(() => {
    let options: BillingItemCompanyBase[] = [];

    const companiesData = getFilteredArrayValueByQueryParam({
      key: 'company',
      currentSelectData: selectsData.companies,
      getExtractorKey: getCompanyToBillingItemId,
    });

    if (companiesData && companiesData.length > 0) {
      const currentCompany = companiesData[0];

      options = options.concat(currentCompany);
      setFilterByKey('company', currentCompany);
    }

    const integrationStoresData = getFilteredArrayValueByQueryParam({
      key: 'integrationStores',
      currentSelectData: selectsData.companies,
      getExtractorKey: getCompanyToBillingItemId,
    });

    if (integrationStoresData && integrationStoresData.length > 0) {
      options = options.concat(integrationStoresData);
      setFilterByKey('integrationStores', integrationStoresData);
    }

    const giverCompaniesData = getFilteredArrayValueByQueryParam({
      key: 'giverCompanies',
      currentSelectData: selectsData.companies,
      getExtractorKey: getCompanyToBillingItemId,
    });

    if (giverCompaniesData && giverCompaniesData.length > 0) {
      options = options.concat(giverCompaniesData);
      setFilterByKey('giverCompanies', giverCompaniesData);
    }

    if (options.length > 0) {
      selectsData.companies.autocompleteRef.current?.setValue(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getFilteredArrayValueByQueryParam,
    selectsData.companies.list,
    selectsData.companies.loading,
    setFilterByKey,
  ]);

  useEffect(() => {
    if (hasAccesMgmBillingGiver) {
      setCompaniesByQueryParam();
    }
  }, [hasAccesMgmBillingGiver, setCompaniesByQueryParam]);

  const setDateByQueryParam = useCallback(
    (key: 'dateFrom' | 'dateTo') => {
      const currentDateQueryParam = getValueQueryParams(key);
      const currentDateDefault = getValueInitialFilters(key);

      if (currentDateQueryParam) {
        const valDate = new Date(currentDateQueryParam);

        const parseValDate =
          valDate.toString() !== 'Invalid Date' ? valDate : currentDateDefault;

        setFilterByKey(key, parseValDate);
      }
    },
    [getValueQueryParams, getValueInitialFilters, setFilterByKey],
  );

  useEffect(() => {
    if (hasAccesMgmBillingGiver) {
      setDateByQueryParam('dateFrom');
      setDateByQueryParam('dateTo');
    }
  }, [hasAccesMgmBillingGiver, setDateByQueryParam]);

  const parseBatchNumberRequest = useCallback(() => {
    const currentTypeDateFilterAppliedBatchNumber =
      getFilterAppliedByKey('batchNumber');

    const currentBatchNumberQueryParam = getValueQueryParams('batchNumber');
    const currentBatchNumberFilter = getValueCurrentFilters('batchNumber');

    const currentBatchNumber =
      currentTypeDateFilterAppliedBatchNumber === 'queryParams'
        ? currentBatchNumberQueryParam
        : currentBatchNumberFilter;

    return currentBatchNumber ? Number(currentBatchNumber) : null;
  }, [getFilterAppliedByKey, getValueQueryParams, getValueCurrentFilters]);

  const parseCompanyRequest = useCallback(() => {
    const currentTypeCompanyFilterAppliedCompany =
      getFilterAppliedByKey('company');

    const currentCompanyQueryParam = getValueQueryParams('company');
    const currentCompanyFilter = getValueCurrentFilters('company');

    let id: Nullable<string> = null;

    if (
      currentTypeCompanyFilterAppliedCompany === 'queryParams' &&
      currentCompanyQueryParam
    ) {
      id = currentCompanyQueryParam;
    }

    if (
      currentTypeCompanyFilterAppliedCompany === 'filters' &&
      currentCompanyFilter
    ) {
      id = getCompanyToBillingItemId(currentCompanyFilter);
    }

    return id;
  }, [getFilterAppliedByKey, getValueQueryParams, getValueCurrentFilters]);

  const parseOthersCompaniesRequest = useCallback(
    (key: 'integrationStores' | 'giverCompanies') => {
      const currentTypeOthersCompaniesFilterAppliedCompanies =
        getFilterAppliedByKey(key);

      let ids: Nullable<string[]> = null;

      const currentOthersCompaniesQueryParam = getValueQueryParams(key);
      const currentOthersCompaniesFilter = getValueCurrentFilters(key);

      if (
        currentTypeOthersCompaniesFilterAppliedCompanies === 'queryParams' &&
        currentOthersCompaniesQueryParam
      ) {
        ids = isArray(currentOthersCompaniesQueryParam)
          ? currentOthersCompaniesQueryParam
          : [currentOthersCompaniesQueryParam];
      }

      if (
        currentTypeOthersCompaniesFilterAppliedCompanies === 'filters' &&
        currentOthersCompaniesFilter &&
        currentOthersCompaniesFilter.length > 0
      ) {
        ids = currentOthersCompaniesFilter.map((company) =>
          getCompanyToBillingItemId(company),
        );
      }

      return ids;
    },
    [getFilterAppliedByKey, getValueCurrentFilters, getValueQueryParams],
  );

  const parseDateRequest = useCallback(
    (key: 'dateFrom' | 'dateTo') => {
      const currentTypeDateFilterAppliedCurrentDate =
        getFilterAppliedByKey(key);

      const currentDateQueryParam = getValueQueryParams(key);
      const currentDateFilter = getValueCurrentFilters(key);

      const currentDate =
        currentTypeDateFilterAppliedCurrentDate === 'queryParams'
          ? currentDateQueryParam
          : currentDateFilter;

      if (!currentDate) return null;

      const formatDate = formatterDate(currentDate, {
        format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
      });

      return formatDate || null;
    },
    [getFilterAppliedByKey, getValueQueryParams, getValueCurrentFilters],
  );

  const parseFiltersRequest = useMemo(
    (): SettlementBillingItemsFilter => ({
      lot: parseBatchNumberRequest(),
      companyIdParam: parseCompanyRequest(),
      companyIntegrationStoreIds:
        parseOthersCompaniesRequest('integrationStores'),
      giverCompanyIds: parseOthersCompaniesRequest('giverCompanies'),
      dateTimeFrom: parseDateRequest('dateFrom'),
      dateTimeTo: parseDateRequest('dateTo'),
    }),
    [
      parseBatchNumberRequest,
      parseCompanyRequest,
      parseDateRequest,
      parseOthersCompaniesRequest,
    ],
  );

  return {
    filters,
    setFilterByKey: setFilterAppliedByKey,
    handleChangeInputFilter: handleChangeInputFilterApplied,
    clearFilters: clearFiltersApplied,
    parseFiltersRequest,
  };
};
